'use client'

import { cn } from '@/lib/utils'
import { EM_DASH } from '@/lib/copy-sanitize'

interface CreditsDisplayProps {
  remaining: number
  total: number
  className?: string
}

/**
 * §19.7 credits — a quota specified under the same rule as install counts
 * (§02.9): the number is real, zero is a normal state with its own copy,
 * and low reads as low from weight and wording, never colour (the system
 * has no amber/red). Reset is DAILY here — that's a product fact (this
 * tool's quota resets at midnight, not monthly), left alone per the brief.
 */
export function CreditsDisplay({ remaining, total, className }: CreditsDisplayProps) {
  const isEmpty = remaining === 0
  const isLow = !isEmpty && remaining <= 3

  return (
    <div className={cn(
      'flex items-baseline justify-between gap-4 border-y border-[color:var(--tls-border-subtle)] py-3',
      className
    )}>
      <div className="flex items-baseline gap-2">
        {/* §19.7 measures: "Balance, 32px weight 800, tabular, em dash at zero".
            The glyph is aria-hidden at zero and paired with a sr-only "Zero"
            sibling, the same pairing §02.9's zero state gets via
            downloadCountAriaLabel: otherwise a screen reader announces
            "em dash" or "dash" instead of a quantity. */}
        <span
          aria-hidden={isEmpty ? true : undefined}
          className={cn(
            'text-[32px] font-extrabold leading-none tabular-nums',
            isEmpty ? 'text-[var(--tls-fg-subtle)]' : 'text-[var(--tls-fg-strong)]'
          )}
        >
          {isEmpty ? EM_DASH : remaining}
        </span>
        {isEmpty && <span className="sr-only">Zero</span>}
        <span className="tls-eyebrow [--tls-eyebrow-color:var(--tls-fg-muted)]">
          {isEmpty ? 'no credits left' : remaining === 1 ? 'voice credit' : 'voice credits'}
        </span>
      </div>
      <div className="text-right">
        <p className={cn('text-sm', isLow || isEmpty ? 'font-semibold text-[var(--tls-fg-strong)]' : 'text-[var(--tls-fg-muted)]')}>
          {isEmpty
            ? `You have used all ${total} today.`
            : `${remaining} of ${total} left today`
          }
        </p>
        <p className="text-sm text-[var(--tls-fg-subtle)]">Resets at midnight.</p>
      </div>
    </div>
  )
}
